import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Drawer, Input, Empty, Spin, Tag, Alert, Typography, Button } from 'antd';
import {
  SearchOutlined,
  ShopOutlined,
  CheckOutlined,
  StarFilled,
  ReloadOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import type { Merchant } from '@/api/types';
import { trackEvent } from '@/lib/telemetry';
import { switchPractice } from './practiceSwitch';

const { Text } = Typography;

const RECENT_KEY = 'agent_studio_recent_merchants';
const RECENT_MAX = 5;

interface Props {
  open: boolean;
  onClose: () => void;
}

// Polished merchant picker. Slide-in drawer from the right with:
//  - Sticky search box at top (Cmd+F or just type)
//  - Pinned "Recent" group based on last 5 selections (localStorage)
//  - Full searchable list below; matches both title and code-name
//  - Each row shows: title (bold) · code (mono) · city, state · current pill
//  - Click row → switches merchant + records recent + closes drawer
//  - Errors and loading rendered with brand-consistent components

export const MerchantPickerDrawer = observer(function MerchantPickerDrawer({ open, onClose }: Props) {
  const { merchantContextStore: ctx, themeStore } = useStore();
  const [query, setQuery] = useState('');
  const inputRef = useRef<{ focus: () => void } | null>(null);
  const [recentIds, setRecentIds] = useState<number[]>(() => loadRecent());

  // Load merchants when drawer opens (lazy — same store as the old switcher).
  useEffect(() => {
    if (open && ctx.merchants.length === 0 && !ctx.isLoading) {
      void ctx.loadMerchants();
    }
    if (open) {
      // Focus the search input shortly after opening (after slide-in animation).
      const t = setTimeout(() => inputRef.current?.focus(), 220);
      return () => clearTimeout(t);
    }
    setQuery('');
    return undefined;
  }, [open, ctx]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ctx.merchants;
    return ctx.merchants.filter((m) =>
      [m.title, m.codeName, m.city, m.province, m.zipCode]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [ctx.merchants, query]);

  const recents = useMemo(() => {
    if (query.trim()) return []; // hide recents while searching
    const lookup = new Map(ctx.merchants.map((m) => [m.id, m]));
    return recentIds
      .map((id) => lookup.get(id))
      .filter((m): m is Merchant => Boolean(m));
  }, [ctx.merchants, recentIds, query]);

  const isDark = themeStore.mode === 'dark';

  function selectMerchant(m: Merchant): void {
    void switchPractice(ctx, m.id, () => {
      trackEvent('Merchant.Switched', { merchantId: m.id, source: 'drawer' });
      const updated = [m.id, ...recentIds.filter((id) => id !== m.id)].slice(0, RECENT_MAX);
      setRecentIds(updated);
      saveRecent(updated);
      onClose();
    });
  }

  return (
    <Drawer
      title={null}
      open={open}
      onClose={onClose}
      placement="right"
      width={460}
      closable={false}
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column' },
        header: { display: 'none' },
      }}
    >
      {/* Sticky header w/ title + search */}
      <div
        style={{
          padding: '16px 20px 12px',
          borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          background: isDark ? colors.slate[800] : '#FFFFFF',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ShopOutlined style={{ fontSize: 18, color: colors.ink[600] }} />
            <Text strong style={{ fontSize: 16 }}>Switch merchant</Text>
          </div>
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined />}
            loading={ctx.isLoading}
            onClick={() => void ctx.loadMerchants()}
          />
        </div>
        <Input
          ref={inputRef as never}
          autoFocus
          allowClear
          size="large"
          prefix={<SearchOutlined style={{ color: colors.slate[400] }} />}
          placeholder="Search by name, code, city…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {ctx.merchants.length > 0 && !query && (
          <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'inline-block' }}>
            {ctx.merchants.length} accessible
          </Text>
        )}
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', padding: '12px 0' }}>
        {ctx.isLoading && ctx.merchants.length === 0 && (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <Spin />
          </div>
        )}

        {ctx.error && (
          <div style={{ padding: '0 20px' }}>
            <Alert
              type="error"
              message="Failed to load merchants"
              description={ctx.error}
              showIcon
              action={
                <Button size="small" type="primary" onClick={() => void ctx.loadMerchants()}>
                  Retry
                </Button>
              }
            />
          </div>
        )}

        {!ctx.isLoading && !ctx.error && ctx.merchants.length === 0 && (
          <div style={{ padding: '40px 20px' }}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No merchants accessible." />
          </div>
        )}

        {recents.length > 0 && (
          <Section label="Recent">
            {recents.map((m) => (
              <MerchantRow
                key={`r-${m.id}`}
                merchant={m}
                selected={m.id === ctx.selectedMerchantId}
                isRecent
                onClick={() => selectMerchant(m)}
                isDark={isDark}
                highlight={query}
              />
            ))}
          </Section>
        )}

        {filtered.length > 0 ? (
          <Section label={query ? `${filtered.length} matches` : 'All merchants'}>
            {filtered.map((m) => (
              <MerchantRow
                key={m.id}
                merchant={m}
                selected={m.id === ctx.selectedMerchantId}
                onClick={() => selectMerchant(m)}
                isDark={isDark}
                highlight={query}
              />
            ))}
          </Section>
        ) : (
          query &&
          ctx.merchants.length > 0 && (
            <div style={{ padding: '40px 20px' }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={`No matches for "${query}".`}
              />
            </div>
          )
        )}
      </div>
    </Drawer>
  );
});

// ── Internal components ───────────────────────────────────────────────────────

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div
        style={{
          padding: '8px 20px 6px',
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: colors.slate[400],
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

interface RowProps {
  merchant: Merchant;
  selected: boolean;
  isRecent?: boolean;
  isDark: boolean;
  highlight: string;
  onClick: () => void;
}

function MerchantRow({ merchant, selected, isRecent, isDark, highlight, onClick }: RowProps) {
  const baseBg = selected ? (isDark ? colors.ink[700] : colors.ink[50]) : 'transparent';
  const hoverBg = isDark ? colors.slate[700] : colors.slate[50];
  const [hovered, setHovered] = useState(false);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor: 'pointer',
        padding: '10px 20px',
        background: hovered ? hoverBg : baseBg,
        borderLeft: `3px solid ${selected ? colors.ink[600] : 'transparent'}`,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        transition: 'background 80ms ease',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>
            <Highlighted text={merchant.title} match={highlight} />
          </span>
          {isRecent && (
            <StarFilled style={{ fontSize: 11, color: colors.amber[500] }} aria-label="Recent" />
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
          {merchant.codeName && (
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                color: colors.slate[500],
              }}
            >
              <Highlighted text={merchant.codeName} match={highlight} />
            </span>
          )}
          {(merchant.city || merchant.province) && (
            <span
              style={{
                fontSize: 12,
                color: colors.slate[500],
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
              }}
            >
              <EnvironmentOutlined style={{ fontSize: 10 }} />
              <Highlighted
                text={[merchant.city, merchant.province].filter(Boolean).join(', ')}
                match={highlight}
              />
            </span>
          )}
        </div>
      </div>
      {selected && <Tag color="blue" icon={<CheckOutlined />}>Current</Tag>}
    </div>
  );
}

function Highlighted({ text, match }: { text?: string; match: string }) {
  if (!text) return null;
  if (!match.trim()) return <>{text}</>;
  const lower = text.toLowerCase();
  const q = match.toLowerCase();
  const idx = lower.indexOf(q);
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark
        style={{
          background: 'rgba(245, 158, 11, 0.25)',
          color: 'inherit',
          padding: '0 1px',
          borderRadius: 2,
        }}
      >
        {text.slice(idx, idx + match.length)}
      </mark>
      {text.slice(idx + match.length)}
    </>
  );
}

// ── localStorage helpers (recent merchants) ───────────────────────────────────

function loadRecent(): number[] {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as number[];
    return Array.isArray(parsed) ? parsed.filter((n) => typeof n === 'number') : [];
  } catch {
    return [];
  }
}

function saveRecent(ids: number[]): void {
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(ids.slice(0, RECENT_MAX)));
  } catch {
    // ignore
  }
}
